// Pure helpers for the year-in-review screen. Kept out of the screen so they're unit-testable
// (see test/yearReview.test.ts) — the screen itself is DOM assembly with no logic worth testing.

// Total tonnage is a number nobody has intuition for: "142,318 kg" is just digits. Anchoring it
// to one concrete object is the whole point of the stat. Masses are rough, publicly-quoted
// figures — the claim is "about this many", and the copy says "about".
const ANCHORS = [
  { kg: 7_300_000, one: "the iron in the Eiffel Tower", many: "Eiffel Towers' worth of iron" },
  { kg: 333_000, one: "a fully loaded Boeing 747", many: "fully loaded Boeing 747s" },
  { kg: 150_000, one: "a blue whale", many: "blue whales" },
  { kg: 40_000, one: "a loaded articulated lorry", many: "loaded articulated lorries" },
  { kg: 6_000, one: "an African elephant", many: "African elephants" },
  { kg: 1_400, one: "a small car", many: "small cars" },
  { kg: 480, one: "a grand piano", many: "grand pianos" },
  { kg: 190, one: "an adult lion", many: "adult lions" },
];

/**
 * The largest anchor the tonnage clears at least once, with how many of them it is.
 * Returns null below the smallest anchor — a first month of training deserves its real number,
 * not "0.4 lions", which reads as a joke at the user's expense.
 */
export function tonnageComparison(kg) {
  if (!Number.isFinite(kg) || kg < ANCHORS[ANCHORS.length - 1].kg) return null;
  const anchor = ANCHORS.find((a) => kg >= a.kg);
  const raw = kg / anchor.kg;
  // One decimal while the count is small (1.4 elephants is a real distinction), whole numbers
  // once it isn't (37.2 lions is false precision on a number that was rounded to begin with).
  const count = raw < 10 ? Math.round(raw * 10) / 10 : Math.round(raw);
  return { count, label: count === 1 ? anchor.one : anchor.many };
}

const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

export function monthName(n, { short = false } = {}) {
  const name = MONTHS[Math.max(1, Math.min(12, n)) - 1];
  return short ? name.slice(0, 3) : name;
}

/**
 * How much of the year the review is actually speaking from. An invite-only app means most
 * users have months, not years — the screen says so up front rather than presenting three
 * months as though it were a year, which would make every superlative sound bigger than it is.
 */
export function coverage(review) {
  const trained = (review?.months || []).filter((m) => m.sessions > 0);
  const now = new Date();
  const isCurrentYear = review?.year === now.getUTCFullYear();
  return {
    monthsTrained: trained.length,
    // A year still running is partial by definition, however long you've been using the app.
    partial: isCurrentYear || trained.length < 6,
    monthsElapsed: isCurrentYear ? now.getUTCMonth() + 1 : 12,
  };
}

/**
 * Plain-text summary for the share sheet / clipboard. Formatters are injected so the text
 * respects the reader's kg/lb setting without this module importing the display layer.
 * Every line after the first is conditional: a short history shares a short message rather
 * than a template with blanks in it.
 */
export function shareText(review, { fmtWeight = (kg) => `${Math.round(kg)} kg`, fmtVolume = fmtWeight } = {}) {
  const lines = [`My ${review.year} in Kashtu`];
  lines.push(`${review.sessions} workout${review.sessions === 1 ? "" : "s"} · ${review.sets} sets · ${fmtVolume(review.volume_kg)} lifted`);
  const cmp = tonnageComparison(review.volume_kg);
  if (cmp) lines.push(`That's about ${cmp.count} ${cmp.label}.`);
  // The heaviest bar, not the best estimate: an unqualified "best lift" in a message someone
  // else reads should be a weight that was actually on the bar.
  if (review.heaviest_set) {
    lines.push(`Heaviest set: ${review.heaviest_set.exercise_name} — ${fmtWeight(review.heaviest_set.weight_kg)} × ${review.heaviest_set.reps}`);
  }
  if (review.best_week_streak > 1) lines.push(`Longest streak: ${review.best_week_streak} weeks on target`);
  if (review.best_month) lines.push(`Best month: ${monthName(review.best_month.month)} (${review.best_month.sessions} workouts)`);
  return lines.join("\n");
}
